import { Divider, Tooltip, TextField } from "@mui/material";
import dateformat from "dateformat";
import { useState, useEffect } from "react";

import { Button, PreparationPDF, Table, Text } from "$components";
import { queryClient } from "$utils/queryClient";
import { EDBService } from "$services";
import useAuth from "$hooks/useAuth";
import { useStyles as useEDBProfileClasses } from "$containers/EDB/styles/EdbProfile.Styles";
import useJStyles from "$hooks/useJStyles";
import { DispositionType, PreparationOrderStatus } from "$types";
import { DISPOSITION_LABELS } from "$utils/typeConstants";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import { useProfileEDBContext } from "../context/ProfileEDBContext";
import InformationTable from "./InformationTable";

const OpHeader = [
  { id: "op_order", label: "Ordre de Préparation" },
  { id: "ref_op_warehouse", label: "Ref Prépa Entrepôt" },
  { id: "in_progress", label: "Prépa. en cours" },
  { id: "dispo", label: "Prépa. dispo" },
  { id: "expedition_in_progress", label: "Expédié" },
  { id: "expedited", label: "Livré" },
  { id: "shipped", label: "Collecté" },
  { id: "action", label: "Action" },
];

function DeliverySummary() {
  const { jclasses } = useJStyles();
  const edbClasses = useEDBProfileClasses();
  const { token } = useAuth();

  const { edb, opList, isDisabled, fetchEdb, integratedOrders } =
    useProfileEDBContext();

  const [analyticCodeValue, setAnalyticCodeValue] = useState(
    edb?.analyticCode || ""
  );

  useEffect(() => {
    setAnalyticCodeValue(edb?.analyticCode || "");
  }, [edb?.analyticCode]);

  const editAnalyticCode = analyticCodeValue !== (edb?.analyticCode || "");

  const saveAnalyticCode = async () => {
    try {
      await EDBService.UpdateEDB(
        token,
        { ...edb, analyticCode: analyticCodeValue, integratedOrders },
        edb._id
      );
      await queryClient.removeQueries({
        queryKey: ["getEdbById", edb._id],
      });
      await fetchEdb();
    } catch (e) {
      console.error("Erreur lors de la sauvegarde du code analytique", e);
    }
  };

  const getDispositionTypeLabel = (dispositionType: DispositionType) => {
    return DISPOSITION_LABELS[dispositionType] ?? null;
  };

  // Render OP status components
  const renderOpStatus = () => {
    const isCancelled = (op: any) =>
      op.status === PreparationOrderStatus.CANCELLED;

    const notAvailableComponent = <Text>-</Text>;

    const renderDateComponent = (status: any, cancelled: boolean) => {
      const hasDate = !!status;
      return (
        <Text
          style={{
            color: cancelled ? "#999" : "inherit",
            textDecoration: cancelled && hasDate ? "line-through" : "none",
          }}
        >
          {hasDate ? dateformat(status.statusDate, DEFAULT_DATE_FORMAT) : "-"}
        </Text>
      );
    };

    const renderTextValue = (value: string, cancelled: boolean) => (
      <Text style={{ color: cancelled ? "#999" : "inherit" }}>{value}</Text>
    );

    const wrapWithTooltipIfCancelled = (content: any, cancelled: boolean) => {
      return cancelled ? (
        <Tooltip title="Cet ordre de préparation a été annulé" arrow>
          <div style={{ width: "100%", height: "100%" }}>{content}</div>
        </Tooltip>
      ) : (
        content
      );
    };

    return (opList || []).map((op: any) => {
      const cancelled = isCancelled(op);

      return [
        {
          value: wrapWithTooltipIfCancelled(
            renderTextValue(op.code || "", cancelled),
            cancelled
          ),
        },
        {
          value: wrapWithTooltipIfCancelled(
            renderTextValue(op.refPreparationEntrepot || "N/A", cancelled),
            cancelled
          ),
        },
        {
          value: wrapWithTooltipIfCancelled(
            (() => {
              if (op.status >= PreparationOrderStatus.IN_PROGRESS) {
                const status = (op.statusHistory || []).find(
                  (st: any) => st.status === PreparationOrderStatus.IN_PROGRESS
                );
                return renderDateComponent(status, cancelled);
              }
              return notAvailableComponent;
            })(),
            cancelled
          ),
        },
        {
          value: wrapWithTooltipIfCancelled(
            (() => {
              if (op.status >= PreparationOrderStatus.PREPARED) {
                const status = (op.statusHistory || []).find(
                  (st: any) => st.status === PreparationOrderStatus.PREPARED
                );
                return renderDateComponent(status, cancelled);
              }
              return notAvailableComponent;
            })(),
            cancelled
          ),
        },
        {
          value: wrapWithTooltipIfCancelled(
            (() => {
              if (op.status >= PreparationOrderStatus.SHIPPED) {
                const status = (op.statusHistory || []).find(
                  (st: any) => st.status === PreparationOrderStatus.SHIPPED
                );
                return renderDateComponent(status, cancelled);
              }
              return notAvailableComponent;
            })(),
            cancelled
          ),
        },
        {
          value: wrapWithTooltipIfCancelled(
            (() => {
              if (op.status >= PreparationOrderStatus.DELIVERED) {
                const status = (op.statusHistory || []).find(
                  (st: any) => st.status === PreparationOrderStatus.DELIVERED
                );
                return renderDateComponent(status, cancelled);
              }
              return notAvailableComponent;
            })(),
            cancelled
          ),
        },
        {
          value: wrapWithTooltipIfCancelled(
            (() => {
              if (op.status >= PreparationOrderStatus.COLLECTED) {
                let status = (op.statusHistory || []).find(
                  (st: any) => st.status === PreparationOrderStatus.COLLECTED
                );
                if (!status) {
                  status = (op.statusHistory || []).find(
                    (st: any) => st.status === PreparationOrderStatus.COLLECTED
                  );
                }
                return renderDateComponent(status, cancelled);
              }
              return notAvailableComponent;
            })(),
            cancelled
          ),
        },
        {
          value: wrapWithTooltipIfCancelled(
            <PreparationPDF opData={op} disabled={cancelled} />,
            cancelled
          ),
        },
      ];
    });
  };

  const preparationTypeRows = [
    {
      label: "Type de préparation souhaitée",
      value: edb?.preparationType,
    },
  ];

  const dispositionTypeRows = [
    {
      label: "Lieu de mise à disposition",
      value: getDispositionTypeLabel(edb?.dispositionType),
    },
  ];

  const contactRecoveryRows = [
    {
      label: "Contact",
      value: `${edb?.firstnameContactRecovery || "N/A"} ${
        edb?.lastnameContactRecovery?.toUpperCase() || "N/A"
      }`,
    },
    {
      label: "Email",
      value: edb?.emailContactRecovery || "N/A",
    },
    {
      label: "Téléphone",
      value: edb?.phoneContactRecovery || "N/A",
    },
  ];

  const dispositionAddressRows = [
    ...(edb?.dispositionType !== DispositionType.NEAREST_WAREHOUSE
      ? [
          ...([
            DispositionType.SUBCONTRACTED_WAREHOUSE,
            DispositionType.OTHER_PLACE,
            DispositionType.COLLECTED,
          ].includes(edb?.dispositionType)
            ? [
                {
                  label:
                    edb?.dispositionType === DispositionType.COLLECTED
                      ? "Entrepôt de préparation"
                      : "Nom entrepôt",
                  value: edb?.dispositionPlace || edb?.entrepotName || "N/A",
                },
              ]
            : []),
          {
            label: "Adresse",
            value: (
              <>
                {edb?.dispositionAdress}
                <br />
                {edb?.dispositionAdress2 && (
                  <>
                    {edb?.dispositionAdress2}
                    <br />
                  </>
                )}
                {edb?.dispositionPostalCode} {edb?.dispositionCity}
                <br />
                {edb?.dispositionCountry}
              </>
            ),
          },
        ]
      : []),
  ];

  // Updated to correctly display exact date or latest date label based on dispositionDateIsExact value
  const dispositionDateRows = [
    {
      label:
        edb?.dispositionDateIsExact === false
          ? "Au plus tard le"
          : "Date exacte",
      value:
        edb?.dispositionDate &&
        dateformat(new Date(edb?.dispositionDate), DEFAULT_DATE_FORMAT),
    },
  ];

  return (
    <div>
      <div className={edbClasses.informations}>
        <div className="about">
          <div className="left">
            <InformationTable rows={preparationTypeRows} />
          </div>
          <div className="right">
            <InformationTable rows={dispositionTypeRows} />
          </div>
        </div>
      </div>
      <Divider className={edbClasses.divider} />
      <div className={edbClasses.informations}>
        <div className="about">
          <div className="left">
            <Text type="h3" className={edbClasses.informationsTitle}>
              Contact de récupération du matériel
            </Text>
            <InformationTable rows={contactRecoveryRows} />
          </div>
          <div className="right">
            {edb?.dispositionType !== DispositionType.NEAREST_WAREHOUSE && (
              <>
                <Text type="h3" className={edbClasses.informationsTitle}>
                  {edb?.dispositionType === DispositionType.COLLECTED
                    ? "Entrepôt de préparation"
                    : "Adresse de mise à disposition"}
                </Text>
                <InformationTable rows={dispositionAddressRows} />
              </>
            )}
            <Text type="h3" className={edbClasses.informationsTitle}>
              Date de mise à disposition souhaitée
            </Text>
            <InformationTable rows={dispositionDateRows} />
          </div>
        </div>
        <Divider className={edbClasses.divider} />
        <div className={edbClasses.informations}>
          <Text type="h3" className={edbClasses.informationsTitle}>
            Code analytique
          </Text>
          <div
            className="about"
            style={{
              marginTop: 8,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <TextField
              style={{ backgroundColor: "white" }}
              value={analyticCodeValue}
              variant="outlined"
              placeholder="N/A"
              disabled={isDisabled}
              inputProps={{ maxLength: 50 }}
              size="small"
              onChange={(e) => setAnalyticCodeValue(e.target.value)}
            />
            {editAnalyticCode && (
              <Button onClick={saveAnalyticCode}>Enregistrer</Button>
            )}
          </div>
        </div>
        <Divider className={edbClasses.divider} />
        <div className={edbClasses.informations}>
          <Text type="h3" className={edbClasses.informationsTitle}>
            Notes, contraintes
          </Text>
          <div className="about" style={{ marginTop: 8 }}>
            {edb?.notes || "N/A"}
          </div>
        </div>
        <Divider className={edbClasses.divider} />
        <div className={jclasses.scrollTable}>
          <Table
            rowsPerPage={1000}
            thead={OpHeader}
            bodyData={renderOpStatus()}
            bodyMissingLabelText="Aucun ordre de préparation n'a encore été créé"
            noPointer
          />
        </div>
        {/* {!opList.length && (
          <div className={classes.NoResultPlaceHolder}>
            <h3>Aucun ordre de préparation n&#39;a encore été créé</h3>
          </div>
        )} */}
      </div>
    </div>
  );
}

export default DeliverySummary;
